import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight } from 'lucide-react';
import { getRegions } from '@/lib/api';

const RegionsPage = () => {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Browse Bali Real Estate by Region | Lalita Properties';
    const meta = document.querySelector('meta[name="description"]');
    const desc = "Explore villas, land, and rentals across Bali's 16 real estate markets — Canggu, Seminyak, Uluwatu, Ubud and more. Live listings with verified inventory.";
    if (meta) meta.setAttribute('content', desc);

    (async () => {
      try {
        const res = await getRegions();
        setRegions(res.data);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('getRegions failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="bg-warm-sand min-h-screen" data-testid="regions-page">
      {/* Hero */}
      <section className="bg-ubud-jungle text-white pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-body uppercase tracking-[0.25em] text-temple-gold text-xs mb-4">
              Browse by Region
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light mb-4 leading-tight">
              Every market in Bali, <em className="text-temple-gold not-italic font-normal">curated</em>.
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Region grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl h-52 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {regions.map((region, idx) => (
                <motion.div
                  key={region.slug}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.03 }}
                >
                  <Link
                    to={`/region/${region.slug}`}
                    className="group block bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full"
                    data-testid={`region-card-${region.slug}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-temple-gold" />
                        <span className="text-xs text-charcoal-muted uppercase tracking-wider">Region</span>
                      </div>
                      <span className="text-[11px] bg-warm-sand text-charcoal-muted px-2 py-0.5 rounded-full font-medium">
                        {region.total} {region.total === 1 ? 'listing' : 'listings'}
                      </span>
                    </div>
                    <h2 className="font-heading text-2xl text-charcoal mb-1 group-hover:text-ubud-jungle transition-colors">
                      {region.name}
                    </h2>
                    {region.tagline && (
                      <p className="text-xs italic text-charcoal-muted mb-3">{region.tagline}</p>
                    )}
                    {region.sub_locations.length > 0 && (
                      <p className="text-[11px] text-charcoal-muted/80 mb-4 leading-relaxed">
                        {region.sub_locations.slice(0, 6).join(' · ')}
                        {region.sub_locations.length > 6 && ' …'}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs">
                      {region.for_sale > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-temple-gold" />
                          {region.for_sale} for sale
                        </span>
                      )}
                      {region.for_rent_long > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-ubud-jungle" />
                          {region.for_rent_long} long-term
                        </span>
                      )}
                      {region.for_rent_short > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          {region.for_rent_short} stays
                        </span>
                      )}
                      {region.total === 0 && (
                        <span className="text-charcoal-muted italic">Coming soon</span>
                      )}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-1 text-sm text-ubud-jungle font-medium group-hover:gap-2 transition-all">
                      Explore {region.name}
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default RegionsPage;
